import { useEffect } from 'react';
import { cn } from '@/lib/utils';
import { formatDeadlineCountdown } from '@/utils/discountExpiry';
import { useDeadlineCountdown } from './useDeadlineCountdown';
import { captureDiscountCountdownShown } from './countdownAnalytics';

/**
 * Compact "Ends in …" pill for the in-app discount banner (ORA-892 test arm).
 * Sits inline beside the banner copy so the shell's fixed banner height still
 * holds; renders nothing once the deadline passes (the banner refetches the
 * discount and, if a next phase exists, mounts a fresh chip for it).
 */
export default function DiscountCountdownChip({
  deadline,
  onExpired,
  className,
}: {
  deadline: Date;
  onExpired?: () => void;
  className?: string;
}) {
  const remaining = useDeadlineCountdown(deadline, onExpired);

  useEffect(() => {
    captureDiscountCountdownShown('banner', deadline.getTime() - Date.now());
  }, [deadline]);

  if (remaining <= 0) return null;

  const formatted = formatDeadlineCountdown(remaining);

  return (
    <span
      role="timer"
      aria-live="off"
      aria-label={`Offer ends in ${formatted}`}
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-full bg-surface-raised/30 px-2 py-0.5 font-heading text-xs font-semibold tabular-nums',
        className
      )}
    >
      Ends in {formatted}
    </span>
  );
}
